import * as yup from "yup";
import { useFormik } from "formik";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Select from "react-select";
import { DEFAULT_MIN_PAYOUT } from "../../utils/constants";

export default function UpdateMinPayoutDialog({ open, onClose, merchant, onSubmit }) {
  const formik = useFormik({
    initialValues: {
      min_payout_amount: merchant?.minimum_payout_amount || [],
    },
    validationSchema: yup.object().shape({
      min_payout_amount: yup.array().of(
        yup.object().shape({
          min_amount: yup
            .number()
            .typeError("Must be a number")
            .positive("Must be a positive number")
            .max(999999999999, "Must be smaller than 999,999,999,999")
            .required("Required field"),
        })
      ),
    }),
    onSubmit: async (values) => {
      let minPayoutArray = values.min_payout_amount;
      if (minPayoutArray && minPayoutArray.length === 0) {
        minPayoutArray = null;
      }
      await onSubmit(merchant.merchant_id, minPayoutArray);
    },
  });

  const { values, touched, errors, handleChange, setFieldValue, handleSubmit, isSubmitting } = formik;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Update Min Payout - {merchant?.merchant_name}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                <Select
                  name="min_payout_amount_select"
                  placeholder="Select Currencies..."
                  getOptionLabel={(option) => option.currency}
                  getOptionValue={(option) => option.id}
                  closeMenuOnSelect={false}
                  isMulti
                  options={DEFAULT_MIN_PAYOUT.map((c) => ({ id: c.currency, currency: c.currency }))}
                  value={(values.min_payout_amount || []).map((c) => ({ id: c.currency, currency: c.currency }))}
                  onChange={(selected) => {
                    const selectedArray = selected || [];
                    const newConfig = selectedArray.map((option) => {
                      const existing = (values.min_payout_amount || []).find((c) => c.currency === option.id);
                      if (existing) return existing;

                      const defaultAmt = DEFAULT_MIN_PAYOUT.find((d) => d.currency === option.id)?.min_amount || "";
                      return { currency: option.id, min_amount: defaultAmt };
                    });
                    setFieldValue("min_payout_amount", newConfig);
                  }}
                  styles={{
                    control: (provided) => ({ ...provided, minHeight: "40px", fontSize: "16px" }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    placeholder: (provided) => ({ ...provided, fontSize: "16px" }),
                    singleValue: (provided) => ({ ...provided, fontSize: "16px" }),
                    option: (provided) => ({ ...provided, fontSize: "16px" }),
                  }}
                  menuPortalTarget={document.body}
                />

                {values.min_payout_amount?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {(values.min_payout_amount || []).map((item, index) => {
                      const fieldName = `min_payout_amount[${index}].min_amount`;
                      const fieldError = errors?.min_payout_amount?.[index]?.min_amount;
                      const fieldTouched = touched?.min_payout_amount?.[index]?.min_amount;
                      const minAmtError = Boolean(fieldTouched && fieldError);

                      return (
                        <TextField
                          key={item.currency}
                          sx={{ m: 1 }}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">{item.currency}</InputAdornment>
                            ),
                          }}
                          label="Min Amount"
                          id={fieldName}
                          name={fieldName}
                          value={item.min_amount}
                          onChange={(e) => {
                            const newConfig = [...(values.min_payout_amount || [])];
                            newConfig[index] = {
                              ...newConfig[index],
                              min_amount: e.target.value === "" ? "" : parseFloat(e.target.value),
                            };
                            setFieldValue("min_payout_amount", newConfig);
                          }}
                          type="number"
                          inputProps={{ min: 0, step: 0.01 }}
                          error={minAmtError}
                          helperText={minAmtError ? fieldError : ""}
                          variant="standard"
                          disabled={isSubmitting}
                        />
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            Update
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
